import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { LenderApprovalTermsBasis, LenderPerformanceReportInput } from '@trnsact/trnsact-shared-types';
import { useLenderPerformanceApprovalTerms } from '../../../../hooks/useLenderPerformanceApprovalTerms';
import { APPROVAL_TERMS_BASIS_DEFAULT, TERMS_COMPARISON_DEFAULT_SORTING } from '../../../../constants';
import { ConversionTableSorting } from '../../../../types';
import { ApprovalTermsSummaryCards } from './ApprovalTermsSummaryCards';
import { LenderTermsComparisonTable } from './LenderTermsComparisonTable';
import { AverageRateByLenderChart } from './AverageRateByLenderChart';
import { TermDistributionByLenderChart } from './TermDistributionByLenderChart';
import { TotalCostComparisonChart } from './TotalCostComparisonChart';

interface Props {
  input: LenderPerformanceReportInput;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const ApprovalTerms = ({ input, onLoadingChange }: Props) => {
  const [sorting, setSorting] = useState<ConversionTableSorting>(TERMS_COMPARISON_DEFAULT_SORTING);
  const [basis, setBasis] = useState<LenderApprovalTermsBasis>(APPROVAL_TERMS_BASIS_DEFAULT);

  const basisInput = useMemo<LenderPerformanceReportInput>(
    () => ({ ...input, filters: { ...input.filters, approvalTermsBasis: basis } }),
    [input, basis]
  );

  const { summary, comparison, averageRate, termDistribution, totalCost, isLoading } =
    useLenderPerformanceApprovalTerms(basisInput, sorting);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <Stack gap="0.5rem">
      <ApprovalTermsSummaryCards basis={basis} resource={summary} onBasisChange={setBasis} />

      <Box data-pdf-page-break>
        <LenderTermsComparisonTable sorting={sorting} resource={comparison} onSortingChange={setSorting} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <AverageRateByLenderChart resource={averageRate} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <TermDistributionByLenderChart resource={termDistribution} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <TotalCostComparisonChart resource={totalCost} />
      </Box>
    </Stack>
  );
};
